import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import { ThemeProvider } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffOutlined';
import loginBg from '@/images/login_bg.png';
import { apiClient } from '@/api/axiosClient';
import { useLocale } from '@/i18n/useLocale';
import { createAppTheme } from '@/theme';
import { errorMessageFrom } from '@/lib/toast';

function buildSchema(t: (key: string) => string) {
  return z
    .object({
      newPassword: z.string().min(6, t('validation.passwordMinLength')),
      confirmPassword: z.string().min(1, t('validation.confirmPasswordRequired')),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t('validation.passwordsMustMatch'),
      path: ['confirmPassword'],
    });
}

type ResetPasswordFormValues = z.infer<ReturnType<typeof buildSchema>>;

const FIELD_RADIUS = '10px';
const PILL_RADIUS = '999px';

export default function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const { direction } = useLocale();
  const cardTheme = createAppTheme('light', direction);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  const schema = buildSchema(t);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const onSubmit = async (values: ResetPasswordFormValues) => {
    if (!token) return;
    setFormError(null);
    try {
      await apiClient.post('/auth/reset-password', { token, newPassword: values.newPassword });
      setSucceeded(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (error) {
      setFormError(errorMessageFrom(error));
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        backgroundImage: `url(${loginBg})`,
        backgroundSize: '100% 100%',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <ThemeProvider theme={cardTheme}>
        <Paper elevation={8} sx={{ width: '100%', maxWidth: 400, borderRadius: '20px', p: 5, bgcolor: '#fff' }}>
          {!token ? (
            <Stack alignItems="center" spacing={2} sx={{ textAlign: 'center' }}>
              <ErrorOutlineIcon sx={{ fontSize: 48, color: 'error.main' }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {t('resetPasswordPage.invalidLinkTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('resetPasswordPage.invalidLinkSubtitle')}
              </Typography>
              <Button
                component={RouterLink}
                to="/forgot-password"
                variant="contained"
                size="large"
                fullWidth
                sx={{ borderRadius: PILL_RADIUS, py: 1.25, mt: 1 }}
              >
                {t('forgotPasswordPage.submit')}
              </Button>
            </Stack>
          ) : succeeded ? (
            <Stack alignItems="center" spacing={2} sx={{ textAlign: 'center' }}>
              <CheckCircleOutlinedIcon sx={{ fontSize: 48, color: 'success.main' }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {t('resetPasswordPage.successTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('resetPasswordPage.successSubtitle')}
              </Typography>
            </Stack>
          ) : (
            <>
              <Stack alignItems="center" spacing={0.75} sx={{ mb: 3 }}>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {t('resetPasswordPage.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  {t('resetPasswordPage.subtitle')}
                </Typography>
                <Box sx={{ width: 40, height: 3, borderRadius: '2px', bgcolor: 'primary.main', mt: 1 }} />
              </Stack>

              <Box component="form" onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
                <Stack spacing={2.5}>
                  <Controller
                    name="newPassword"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type={showPassword ? 'text' : 'password'}
                        placeholder={t('resetPasswordPage.newPassword')}
                        error={Boolean(errors.newPassword)}
                        helperText={errors.newPassword?.message}
                        fullWidth
                        autoFocus
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockOutlinedIcon fontSize="small" color="action" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" size="small">
                                  {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                              </InputAdornment>
                            ),
                            sx: { borderRadius: FIELD_RADIUS },
                          },
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="confirmPassword"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder={t('resetPasswordPage.confirmPassword')}
                        error={Boolean(errors.confirmPassword)}
                        helperText={errors.confirmPassword?.message}
                        fullWidth
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockOutlinedIcon fontSize="small" color="action" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowConfirmPassword((v) => !v)} edge="end" size="small">
                                  {showConfirmPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                              </InputAdornment>
                            ),
                            sx: { borderRadius: FIELD_RADIUS },
                          },
                        }}
                      />
                    )}
                  />

                  {formError ? (
                    <Typography variant="body2" color="error">
                      {formError}
                    </Typography>
                  ) : undefined}

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting}
                    sx={{ borderRadius: PILL_RADIUS, py: 1.25 }}
                  >
                    {isSubmitting ? t('resetPasswordPage.submitting') : t('resetPasswordPage.submit')}
                  </Button>

                  <Link component={RouterLink} to="/login" variant="body2" underline="hover" sx={{ textAlign: 'center' }}>
                    {t('forgotPasswordPage.backToLogin')}
                  </Link>
                </Stack>
              </Box>
            </>
          )}

          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} sx={{ mt: 4 }}>
            <GppGoodOutlinedIcon fontSize="small" color="disabled" />
            <Typography variant="body2" color="text.secondary" align="center">
              {t('footer')}
            </Typography>
          </Stack>
        </Paper>
      </ThemeProvider>
    </Box>
  );
}
